import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Constellation / network: a cloud of nodes that drift with noise and connect
 * with lines (precomputed by proximity). Nodes and edges use the SAME drift
 * function, so the lines follow the nodes. Edges and nodes pulse with the beat
 * and the energy.
 */
export class Constellation extends BasePrimitive {
  object3D: THREE.Group
  private points: THREE.Points
  private lines: THREE.LineSegments
  private pointMat: THREE.ShaderMaterial
  private lineMat: THREE.ShaderMaterial

  // Shared drift: same displacement for a given point.
  private static DRIFT = /* glsl */ `
    ${snoise}
    uniform float uTime, uLevel, uReactivity, uBass, uBeat, uBassPunch, uSeedPulse;
    uniform vec3 uSeed;
    vec3 drift(vec3 p) {
      float t = uTime * 0.12;
      vec3 q = p * 0.45;
      vec3 d = vec3(snoise(q + t), snoise(q + t + 11.3), snoise(q + t + 27.1));
      vec3 base = p + d * (0.35 + uLevel * 0.5) * uReactivity;
      // Seed-driven beat punch only (uBeat decays to 0 between beats, so the base
      // cloud stays original); the seed sets which nodes/edges pop out on the beat.
      float lobe = snoise(normalize(p) * 1.6 + uSeed);
      base += normalize(p) * lobe * uSeedPulse * uReactivity * 0.6;
      return base;
    }
  `

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const shared = uniforms as unknown as Record<string, THREE.IUniform>

    this.pointMat = new THREE.ShaderMaterial({
      uniforms: shared,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aSeed;
        uniform float uPixelRatio, uPointScale;
        varying float vSeed;
        varying vec3 vDir;
        ${Constellation.DRIFT}
        ${ZONE_GLSL_COMMON}
        void main() {
          vSeed = aSeed;
          vec3 dir = normalize(position);
          vDir = dir;
          // When zone reaction is on, freeze the nodes to their base positions —
          // only zones react.
          vec3 pos = mix(drift(position), position, uZoneEnabled) + dir * zoneDisp(dir) * 0.6
            + dir * (sin(dot(position, vec3(1.6, 2.2, 1.4)) + uTime * 0.6)
                   + sin(dot(position, vec3(2.8, 1.2, 2.0)) - uTime * 0.5)) * 0.04;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float s = 5.0 + uBass * uBassPunch * 8.0 + uBeat * 6.0;
          gl_PointSize = clamp(s * (6.0 / -mv.z) * (0.6 + aSeed * 0.8), 2.5, 26.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble;
        varying float vSeed;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          vec3 col = hsv2rgb(vec3(fract(uHue + vSeed * 0.1 + uTreble * 0.1), 0.7, 0.9));
          vec4 z = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col), z.rgb, z.a);
          gl_FragColor = vec4(outc, max(core * (uPointSolid > 0.5 ? 1.0 : 0.9), z.a * core));
        }
      `,
    })

    this.lineMat = new THREE.ShaderMaterial({
      uniforms: shared,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aLen;   // original edge length
        varying float vLen;
        varying vec3 vDir;
        ${Constellation.DRIFT}
        ${ZONE_GLSL_COMMON}
        void main() {
          vLen = aLen;
          vec3 dir = normalize(position);
          vDir = dir;
          // When zone reaction is on, freeze the nodes to their base positions —
          // only zones react.
          vec3 pos = mix(drift(position), position, uZoneEnabled) + dir * zoneDisp(dir) * 0.6
            + dir * (sin(dot(position, vec3(1.6, 2.2, 1.4)) + uTime * 0.6)
                   + sin(dot(position, vec3(2.8, 1.2, 2.0)) - uTime * 0.5)) * 0.04;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uLevel, uBeat;
        varying float vLen;
        varying vec3 vDir;
        ${hsv2rgb}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          // Shorter edges are more solid; all of them glow with energy/beat.
          float lenFade = smoothstep(1.4, 0.2, vLen);
          float a = lenFade * (0.12 + uLevel * 0.4 + uBeat * 0.5);
          vec3 col = hsv2rgb(vec3(fract(uHue + 0.05), 0.6, 0.8));
          vec4 z = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col), z.rgb, z.a);
          gl_FragColor = vec4(outc, max(a, z.a));
        }
      `,
    })

    this.points = new THREE.Points(new THREE.BufferGeometry(), this.pointMat)
    this.lines = new THREE.LineSegments(new THREE.BufferGeometry(), this.lineMat)
    this.points.frustumCulled = false
    this.lines.frustumCulled = false
    this.object3D = new THREE.Group()
    this.object3D.add(this.lines)
    this.object3D.add(this.points)
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    // Solid affects the nodes only; the edges stay additive glows.
    const solid = params.pointSolid === true
    this.pointMat.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.pointMat.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const n = Math.floor(320 * (params.density ?? 1))
    const radius = 3
    const pos: number[] = []
    const seed: number[] = []
    const pts: THREE.Vector3[] = []
    for (let i = 0; i < n; i++) {
      // Random point inside a ball.
      const v = new THREE.Vector3(rand(), rand(), rand())
      if (v.lengthSq() > 1) {
        i--
        continue
      }
      v.multiplyScalar(radius)
      pts.push(v)
      pos.push(v.x, v.y, v.z)
      seed.push(Math.random())
    }

    // Edges by proximity (O(n^2) at build time, n is small). Capped.
    const connectDist = 1.25
    const maxEdges = 5000
    const linePos: number[] = []
    const lineLen: number[] = []
    outer: for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dl = pts[i].distanceTo(pts[j])
        if (dl < connectDist) {
          linePos.push(pts[i].x, pts[i].y, pts[i].z, pts[j].x, pts[j].y, pts[j].z)
          lineLen.push(dl, dl)
          if (linePos.length / 6 >= maxEdges) break outer
        }
      }
    }

    const pg = this.points.geometry
    pg.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
    pg.setAttribute('aSeed', new THREE.Float32BufferAttribute(seed, 1))
    pg.computeBoundingSphere()

    const lg = this.lines.geometry
    lg.setAttribute('position', new THREE.Float32BufferAttribute(linePos, 3))
    lg.setAttribute('aLen', new THREE.Float32BufferAttribute(lineLen, 1))
    lg.computeBoundingSphere()
  }
}

/** Random in -1..1 */
function rand(): number {
  return Math.random() * 2 - 1
}
